// ═══════════════════════════════════════════════════════════════════════════
// OPTIMIZED EMPLOYEE CREATION DIALOG - NO TYPING DELAY
// ═══════════════════════════════════════════════════════════════════════════
// Separate component prevents parent table from re-rendering on every keystroke
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, memo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { CreateDialog, FieldLabel } from "./shared";

const EMPLOYEE_DESIGNATION_OPTIONS = [
  { value: "Operator", label: "Operator" },
  { value: "Asistant Operator", label: "Asistant Operator" },
  { value: "Helper", label: "Helper" },
  { value: "machinesupervisor", label: "Machine Supervisor" },
  { value: "Supervisor", label: "Supervisor" },
  { value: "HR", label: "HR" },
  { value: "Planner", label: "Planner" },
  { value: "Admin", label: "Admin" },
];

// ✅ Separate component - parent won't re-render on typing
const EmployeeCreateDialog = memo(function EmployeeCreateDialog({
  open,
  onClose,
  onSave,
  saving,
}) {
  // ✅ Local state - only this dialog re-renders on typing
  const [form, setForm] = useState({
    EmpID: "",
    Name: "",
    MachineGroup: "",
    Designation: "Operator",
    ActiveStatus: 1,
  });

  const handleClose = () => {
    // Reset form when closing
    setForm({
      EmpID: "",
      Name: "",
      MachineGroup: "",
      Designation: "Operator",
      ActiveStatus: 1,
    });
    onClose();
  };

  const handleSave = () => {
    onSave(form);
    // Reset form after save
    setForm({
      EmpID: "",
      Name: "",
      MachineGroup: "",
      Designation: "Operator",
      ActiveStatus: 1,
    });
  };

  return (
    <CreateDialog
      open={open}
      onClose={handleClose}
      title="Create Employee"
      icon={<PersonAddIcon />}
      color="#059669"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Employee ID *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.EmpID}
            onChange={(e) => setForm((f) => ({ ...f, EmpID: e.target.value }))}
            autoFocus
          />
        </Grid>

        <Grid item xs={12} sm={8}>
          <FieldLabel>Name *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.Name}
            onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Machine Group *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.MachineGroup}
            onChange={(e) =>
              setForm((f) => ({ ...f, MachineGroup: e.target.value }))
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Designation</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={form.Designation}
              onChange={(e) =>
                setForm((f) => ({ ...f, Designation: e.target.value }))
              }
            >
              {EMPLOYEE_DESIGNATION_OPTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Active Status</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={form.ActiveStatus}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  ActiveStatus: Number(e.target.value),
                }))
              }
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ textTransform: "none" }}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {saving ? "Creating..." : "Create Employee"}
        </Button>
      </Box>
    </CreateDialog>
  );
});

export default EmployeeCreateDialog;
